import type {
  OnboardingCardState,
  OnboardingEntryPoint,
  OnboardingStep,
} from "@ifgengineering/client-analytics/src/types";

const ENTRY_POINT_KEY = "onboarding_v2b_entry_point";
const SHOWN_CARDS_KEY = "onboarding_v2b_shown_cards";
const REPORTED_STEPS_KEY = "onboarding_v2b_reported_steps";
const COMPLETED_ANCHOR_KEY = "onboarding_v2b_completed_anchor";

type StorageKind = "localStorage" | "sessionStorage";

// Stands in for browser storage when private mode or blocked site data deny it.
const inMemory = new Map<string, string>();

const read = (kind: StorageKind, key: string): string | null => {
  try {
    return window[kind].getItem(key);
  } catch {
    return inMemory.get(`${kind}:${key}`) ?? null;
  }
};

const write = (kind: StorageKind, key: string, value: string): void => {
  try {
    window[kind].setItem(key, value);
  } catch {
    inMemory.set(`${kind}:${key}`, value);
  }
};

const readJson = <T>(kind: StorageKind, key: string, fallback: T): T => {
  try {
    const stored = read(kind, key);
    return stored ? (JSON.parse(stored) as T) : fallback;
  } catch {
    return fallback;
  }
};

const ENTRY_POINTS: readonly OnboardingEntryPoint[] = [
  "signup_gate",
  "homepage",
  "deal_details",
  "resume",
];

/** Where the investor last set off into a step from, for the steps' own events this session. */
export const rememberEntryPoint = (entryPoint: OnboardingEntryPoint): void =>
  write("sessionStorage", ENTRY_POINT_KEY, entryPoint);

export const recallEntryPoint = (): OnboardingEntryPoint | null => {
  const stored = read("sessionStorage", ENTRY_POINT_KEY);
  return ENTRY_POINTS.find((entryPoint) => entryPoint === stored) ?? null;
};

type ShownCards = Record<string, OnboardingCardState>;

/** The card each deal last showed on this device, kept past the anchor so its unlock can be told. */
export const rememberShownCard = (
  dealId: string,
  cardState: OnboardingCardState
): void => {
  const shown = readJson<ShownCards>("localStorage", SHOWN_CARDS_KEY, {});
  if (shown[dealId] === cardState) return;
  write(
    "localStorage",
    SHOWN_CARDS_KEY,
    JSON.stringify({ ...shown, [dealId]: cardState })
  );
};

/** Reads and forgets the deal's last card, so an unlock is told once. */
export const takeShownCard = (dealId: string): OnboardingCardState | null => {
  const shown = readJson<ShownCards>("localStorage", SHOWN_CARDS_KEY, {});
  const cardState = shown[dealId];
  if (!cardState) return null;
  const rest = { ...shown };
  delete rest[dealId];
  write("localStorage", SHOWN_CARDS_KEY, JSON.stringify(rest));
  return cardState;
};

/** True the first time a deal's step is reported done this session; a step finishes once. */
export const markStepReported = (
  dealId: string,
  step: OnboardingStep
): boolean => {
  const reported = readJson<string[]>("sessionStorage", REPORTED_STEPS_KEY, []);
  const key = `${dealId}:${step}`;
  if (reported.includes(key)) return false;
  write(
    "sessionStorage",
    REPORTED_STEPS_KEY,
    JSON.stringify([...reported, key])
  );
  return true;
};

/** The anchoring deal outlives onboarding here, so a later invest can say it was the one. */
export const rememberCompletedAnchor = (dealId: string): void =>
  write("localStorage", COMPLETED_ANCHOR_KEY, dealId);

export const recallCompletedAnchor = (): string | null =>
  read("localStorage", COMPLETED_ANCHOR_KEY);
